// =============================================================================
// MODAL COMPONENT - Consistent modal/dialog styling
// =============================================================================

import { useEffect, useCallback, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IconButton } from './Button.js';
import { useFocusTrap } from '../../hooks/useFocusTrap.js';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-[90vw] max-h-[90vh]',
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: ModalSize;
  children: ReactNode;
  /** Show close button in header */
  showCloseButton?: boolean;
  /** Close on backdrop click */
  closeOnBackdrop?: boolean;
  /** Close on Escape key */
  closeOnEscape?: boolean;
  /** Footer content */
  footer?: ReactNode;
  /** Additional class for the modal content */
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  children,
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  footer,
  className = '',
}: ModalProps) {
  const { t } = useTranslation();
  const focusTrapRef = useFocusTrap(open);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
      }
    },
    [onClose, closeOnEscape]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      ref={focusTrapRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink-1/60 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal content */}
      <div
        className={`
          relative w-full ${sizeClasses[size]}
          bg-surface-3 text-ink-1 rounded-2xl shadow-3
          border border-line-1
          animate-in fade-in zoom-in-95 duration-200
          ${className}
        `}
        onClick={(e) => { e.stopPropagation(); }}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between p-4 border-b border-line-1">
            {title && (
              <h2 id="modal-title" className="font-display text-lg font-semibold text-ink-1">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <IconButton
                icon={<X className="w-5 h-5" />}
                label={t('common.close', 'Close')}
                onClick={onClose}
                className="ml-auto -mr-2"
              />
            )}
          </div>
        )}

        {/* Body */}
        <div className="p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 p-4 border-t border-line-1">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// Lightbox variant for images
interface LightboxModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function LightboxModal({ open, onClose, children }: LightboxModalProps) {
  const { t } = useTranslation();
  const lightboxFocusTrapRef = useFocusTrap(open);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={lightboxFocusTrapRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-1/90"
      role="dialog"
      aria-modal="true"
      aria-label={t('common.imageViewer', { defaultValue: 'Image viewer' })}
      onClick={onClose}
    >
      <IconButton
        icon={<X className="w-6 h-6" />}
        label={t('common.close', 'Close')}
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:bg-white/10"
      />
      <div onClick={(e) => { e.stopPropagation(); }}>{children}</div>
    </div>
  );
}
